// @ts-strict-ignore
import { captureException } from '@sentry/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { MehOutlined, CommentOutlined } from '@ant-design/icons';
import { antGrey } from './styles/globalStyles';
import Result from 'antd/lib/result';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { dayjs } from '@/extendedDayjs';
import { userIsNewUser } from '../timeHelpers';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';

export const ErrorFallback = ({ showRedirectLink }: { showRedirectLink?: boolean }) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: 1,
                height: '100%',
            }}
        >
            <Result
                icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                title="Something went wrong"
                extra={
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: 400,
                            gap: 26,
                        }}
                    >
                        <Text type="secondary">
                            Our engineers have been notified! You can try refreshing the page – if the issue persists,
                            don&apos;t hesitate to get in touch.
                        </Text>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignContent: 'center',
                                gap: 12,
                                width: 200,
                            }}
                        >
                            <Button href={'#hs-chat-open'} type="primary" icon={<CommentOutlined />}>
                                Chat with us
                            </Button>
                            {showRedirectLink ?
                                <Link to="/">Back to dashboard</Link>
                            :   null}
                        </div>
                    </div>
                }
            />
        </div>
    );
};

enum ErrorPriority {
    Normal = 'normal',
    Critical = 'critical',
}

interface State {
    hasError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        captureException(error, {
            tags: {
                priority: this.props.isNewUser ? ErrorPriority.Critical : ErrorPriority.Normal,
            },
        });
    }

    render() {
        if (this.state.hasError) {
            return <ErrorFallback showRedirectLink={this.props.showRedirectLink} />;
        }

        return this.props.children;
    }
}

interface ErrorBoundaryProps {
    showRedirectLink?: boolean;
    children?: React.ReactNode;
}

const mapProps = (state: RootState, ownProps: ErrorBoundaryProps) => {
    const now = dayjs().toISOString();
    const isNewUser = state.user ? userIsNewUser(state.user, now) : false;

    return {
        isNewUser,
        ...ownProps,
    };
};

const connector = connect(mapProps);

export type Props = ConnectedProps<typeof connector>;

export default connector(ErrorBoundary);

export const ErrorBoundaryHOC = <T extends object>(Component: React.ComponentType<T>, errorBoundaryProps?: Props) => {
    return (props: T) => {
        return (
            <ErrorBoundary {...errorBoundaryProps}>
                <Component {...props} />
            </ErrorBoundary>
        );
    };
};
